import { NextSeo } from "next-seo";

import JsonLd from "@/components/seo/JsonLd";
import { BLOG_DEFAULT_IMAGE, BLOG_NAME, absoluteBlogUrl, postPath } from "@/lib/blog/paths";
import type { BlogTopic } from "@/lib/blog/topics";
import type { BlogPostSummary } from "@/lib/ghost";
import { breadcrumbList, collectionPage, type BreadcrumbItem } from "@/lib/seo/schema";

export type ArchiveCopy = {
  title: string;
  description: string;
  heading: string;
  intro: string;
};

const INDEX_DESCRIPTION =
  "Guides on short-form video, faceless channels, AI voiceovers, and growing an audience on TikTok, YouTube Shorts, and Instagram Reels.";

const withPageSuffix = (description: string, page: number, pages: number) =>
  page > 1 ? `${description} Page ${page} of ${pages}.` : description;

export const indexCopy = (page: number, pages: number): ArchiveCopy => ({
  title:
    page > 1
      ? `Crayo Blog, Page ${page}: Short-Form Video Guides`
      : "Crayo Blog: Short-Form Video and Creator Growth Guides",
  description: withPageSuffix(INDEX_DESCRIPTION, page, pages),
  heading: BLOG_NAME,
  intro: page > 1 ? `Page ${page} of ${pages} of the Crayo blog archive.` : INDEX_DESCRIPTION,
});

export const topicCopy = (topic: BlogTopic, page: number, pages: number): ArchiveCopy => ({
  title: page > 1 ? `${topic.name}, Page ${page} | ${BLOG_NAME}` : topic.title,
  description: withPageSuffix(topic.description, page, pages),
  heading: topic.name,
  intro: page > 1 ? `Page ${page} of ${pages} of ${topic.name} guides.` : topic.intro,
});

export const authorCopy = (name: string, page: number, pages: number): ArchiveCopy => ({
  title: page > 1 ? `Posts by ${name}, Page ${page} | ${BLOG_NAME}` : `Posts by ${name} | ${BLOG_NAME}`,
  description: withPageSuffix(
    `Every post written by ${name} for the Crayo blog, covering short-form video, faceless channels, AI voiceovers, and creator growth.`,
    page,
    pages,
  ),
  heading: name,
  intro:
    page > 1
      ? `Page ${page} of ${pages} of posts by ${name}.`
      : `Writer at Crayo. Browse every post published by ${name}.`,
});

type ArchiveSeoProps = {
  copy: ArchiveCopy;
  path: string;
  posts: BlogPostSummary[];
  breadcrumbs: BreadcrumbItem[];
};

export const ArchiveSeo = ({ copy, path, posts, breadcrumbs }: ArchiveSeoProps) => {
  const url = absoluteBlogUrl(path);

  return (
    <>
      <NextSeo
        title={copy.title}
        description={copy.description}
        canonical={url}
        openGraph={{
          type: "website",
          url,
          title: copy.title,
          description: copy.description,
          siteName: "Crayo",
          locale: "en_US",
          images: [{ url: BLOG_DEFAULT_IMAGE, alt: copy.heading }],
        }}
        twitter={{ cardType: "summary_large_image", handle: "@crayoai", site: "@crayoai" }}
      />
      <JsonLd
        nodes={[
          collectionPage({
            url: path,
            name: copy.title,
            description: copy.description,
            items: posts.map((post) => ({
              name: post.title,
              url: postPath(post.slug),
              description: post.excerpt,
            })),
          }),
          ...(breadcrumbs.length ? [breadcrumbList(breadcrumbs)] : []),
        ]}
      />
    </>
  );
};
